'use client';

import { createContext, type ReactNode, useContext, useMemo } from 'react';

export type CommandConflictInfo = {
  id: string;
  displayLabel: string;
  category: string;
  isCustomizable?: boolean;
};

export type ActiveConflicts = Record<string, CommandConflictInfo[]>;

type CheckResetConflictsFn = (
  ids: ReadonlyArray<string>
) => Record<string, CommandConflictInfo[]>;

type KeyboardShortcutsContextType = {
  /** Returns any commands (other than forCommandId) that currently own the given key combo. */
  checkWouldConflict: (
    keys: string[],
    forCommandId: string
  ) => CommandConflictInfo[];
  /**
   * For each id in `ids`, returns the conflicts that would arise if that
   * shortcut were reverted to its registered default *while every other id in
   * `ids` is also reset*. Ids that wouldn't introduce a conflict are omitted
   * from the result. Lets the reset buttons mirror the same allowed-pair
   * rules as live typing.
   */
  checkResetConflicts: CheckResetConflictsFn;
};

const KeyboardShortcutsContext =
  createContext<KeyboardShortcutsContextType | null>(null);

export const KeyboardShortcutsProvider = ({
  children,
  checkWouldConflict,
  checkResetConflicts,
}: {
  children: ReactNode;
  checkWouldConflict: (
    keys: string[],
    forCommandId: string
  ) => CommandConflictInfo[];
  checkResetConflicts: CheckResetConflictsFn;
}) => {
  const value = useMemo(
    () => ({ checkWouldConflict, checkResetConflicts }),
    [checkWouldConflict, checkResetConflicts]
  );

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

export const useKeyboardShortcuts = () => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error(
      'useKeyboardShortcuts must be used within a KeyboardShortcutsProvider'
    );
  }
  return context;
};
